/**
 * The money block under an invoice total once something has been paid:
 *
 *   Total price            $1,485.00
 *   Deposit paid             − $300.00
 *   Partial payment (date)   − $400.00
 *   Balance due              $785.00
 *
 * One rule for every customer surface: the printed invoice, the customer portal,
 * the invoice email and the Square pay link.
 *
 * MIRROR: supabase/functions/_shared/payment-breakdown.ts must stay identical
 * (edge functions cannot import from src). The parity test next to the mirror
 * runs both copies against the same cases.
 */

export interface BreakdownPayment {
  amount: number | string | null;
  /** Date or timestamp; formatted by the caller. */
  payment_date?: string | null;
  notes?: string | null;
}

export type BreakdownRowKind = 'deposit' | 'partial' | 'paid';

export interface BreakdownRow {
  kind: BreakdownRowKind;
  label: string;
  amount: number;
  /** Raw payment date when the row is a single payment, else null. */
  date: string | null;
}

export interface PaymentBreakdown {
  /** 'Total price' when payment rows follow it, else 'Total'. */
  totalLabel: string;
  rows: BreakdownRow[];
  paid: number;
  balance: number;
}

export interface PaymentBreakdownOptions {
  /** invoices.amount_paid. Can lag the ledger when a payment was added on the job. */
  invoicePaid?: number | string | null;
  /** Every job_payments row for the invoice's job (deposits, on-site, office). */
  payments?: BreakdownPayment[] | null;
  /** Paid or refunded: nothing is owed even if no payment row explains it. */
  settled?: boolean;
}

/** More than this many payments collapse into one line so the totals block stays short. */
const MAX_ITEMISED = 4;

const round2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** Rows written by attach_estimate_deposit: "Deposit via Square for EST-…", "Deposit refund via …". */
export function isDepositPayment(p: BreakdownPayment): boolean {
  return /^deposit( refund)? via /i.test((p.notes ?? '').trim());
}

export function paymentBreakdown(totalAmount: number | string | null, opts: PaymentBreakdownOptions = {}): PaymentBreakdown {
  const total = round2(num(totalAmount));
  const list = (opts.payments ?? []).filter((p) => round2(num(p.amount)) !== 0);
  const ledger = round2(list.reduce((s, p) => s + num(p.amount), 0));
  let paid = round2(Math.max(num(opts.invoicePaid), ledger, 0));
  if (opts.settled && paid < total) paid = total;
  const balance = Math.max(round2(total - paid), 0);
  if (paid <= 0) return { totalLabel: 'Total', rows: [], paid: 0, balance };

  const oneLine = (): PaymentBreakdown => ({
    totalLabel: 'Total price',
    rows: [{ kind: balance > 0 ? 'partial' : 'paid', label: balance > 0 ? 'Partial payment' : 'Paid', amount: Math.min(paid, total), date: null }],
    paid,
    balance,
  });

  // Itemise only when the ledger explains the whole amount with plain payments.
  // Refunds, overpayments and hand-marked invoices print as one line instead.
  const itemise =
    list.length > 0 && list.every((p) => num(p.amount) > 0) && Math.abs(ledger - paid) < 0.005 && paid <= total + 0.005;
  if (!itemise) return oneLine();

  const byDate = (a: BreakdownPayment, b: BreakdownPayment) => String(a.payment_date ?? '').localeCompare(String(b.payment_date ?? ''));
  if (list.length === 1 && balance === 0) {
    return { totalLabel: 'Total price', rows: [{ kind: 'paid', label: 'Paid', amount: ledger, date: list[0].payment_date ?? null }], paid, balance };
  }

  const rows: BreakdownRow[] = [];
  const deposits = list.filter(isDepositPayment).sort(byDate);
  const others = list.filter((p) => !isDepositPayment(p)).sort(byDate);
  if (deposits.length) {
    rows.push({
      kind: 'deposit',
      label: 'Deposit paid',
      amount: round2(deposits.reduce((s, p) => s + num(p.amount), 0)),
      date: deposits.length === 1 ? deposits[0].payment_date ?? null : null,
    });
  }
  const kind: BreakdownRowKind = balance > 0 ? 'partial' : 'paid';
  if (others.length > MAX_ITEMISED) {
    rows.push({
      kind,
      label: balance > 0 ? 'Partial payments' : 'Payments received',
      amount: round2(others.reduce((s, p) => s + num(p.amount), 0)),
      date: null,
    });
  } else {
    for (const p of others) {
      rows.push({ kind, label: balance > 0 ? 'Partial payment' : 'Payment received', amount: round2(num(p.amount)), date: p.payment_date ?? null });
    }
  }
  return { totalLabel: 'Total price', rows, paid, balance };
}
